import Vue from 'vue'
import axios from 'axios'
import { get } from 'lodash'

/**
 * The Store Pattern
 * actions -> mutations
 * Never mutate the store directly.
 */

// initial state
// state.stores
const state = {
  list: [],
  new: {},
  store: {}
}

// getters
const getters = {
}

// actions - can be async
const actions = {
  /** load all address based for this user */
  LOAD_ALL_ADDRESSES ({ COMMIT }) {
    var vm = this
    vm.commit('errors/CLEAR_ERRORS')

    // don't load addresses for cold users
    if (window.heb.data.user.isGuest) {
      return
    }

    axios
      .all([
        axios.get('/commerce-api/v1/checkout/shippingAddress')
      ])
      .then(
        axios.spread(({ data }) => {
          vm.commit('deliveryAddress/SET_ADDRESSES_LIST', data.savedAddresses)
        })
      )
      .catch((error) => {
        error.type = 'deliveryAddress'
        vm.dispatch('errors/HANDLE_ERROR', error)
      })
  },

  /**
   * check if address has delivery available
   * @param {object} deliveryAddress - address to check
   */
  CHECK_DELIVERY_AVAILABILITY ({ COMMIT }, deliveryAddress) {
    var vm = this;

    axios
      .all([
        axios.post('/commerce-api/v1/delivery/available', deliveryAddress)
      ])
      .then(
        axios.spread((response) => {
          const { data } = response;
          const resolvedAddress = data.resolvedAddress || {};
          const newAddress = {
            address1: resolvedAddress.streetAddress || deliveryAddress.address1,
            address2: deliveryAddress.address2,
            city: resolvedAddress.addressLocality || deliveryAddress.city,
            state: resolvedAddress.addressRegion || deliveryAddress.state,
            postalCode: resolvedAddress.postalCode ? resolvedAddress.postalCode.toString() : deliveryAddress.postalCode,
            placeId: resolvedAddress.placeId || deliveryAddress.placeId,
            userInputAddres: deliveryAddress.userInputAddress,
          };

          vm.commit('errors/CLEAR_ERRORS')
          vm.commit('deliveryAddress/SET_NEW_ADDRESS', newAddress)
          vm.commit('deliveryAddress/SET_STORE', data)
        })
      )
      .catch((error) => {
        error.type = 'deliveryAddress'
        vm.dispatch('errors/HANDLE_ERROR', error)
      })
  },

  /**
   * update the server with a new pickup store
   * @param {object} deliveryAddress - address to add
   * @param {boolean} [saveAddress] - should we save this address for future use?
   * @param {boolean} [ignoreCartChangeWarnings] - should we ignore cart warnings?
   * @return {promise}
   */
  POST_ADDRESS_CHANGE ({ COMMIT }, { deliveryAddress, saveAddress = false, ignoreCartChangeWarnings = false }) {
    var vm = this
    vm.commit('errors/CLEAR_ERRORS')
    vm.commit('timeslots/SET_FETCHING_TIMESLOTS', true)

    const data = {
      deliveryAddress,
      saveAddress,
      ignoreCartChangeWarnings
    }

    return axios
      .all([
        axios.post('/commerce-api/v1/cart/fulfillment/delivery', data)
      ])
      .then(
        axios.spread((response) => {
          const { status, data } = response

          // immediatley update data used by the template
          vm.commit('reservation/UPDATE_LOCATION', deliveryAddress)
          vm.commit('reservation/UPDATE_FULFILLMENT_TYPE', 'delivery')
          vm.commit('reservation/UPDATE_STORE', data.store)

          // reload the whole cart
          vm.dispatch('cart/LOAD_CART')

          // update timeslots as the delivery store may have changed when providing a new address
          vm.dispatch('timeslots/LOAD_ALL_TIMESLOTS')

          // reload all addresses
          vm.dispatch('deliveryAddress/LOAD_ALL_ADDRESSES', data)

          return status
        })
      )
      .catch((error) => {
        const deliveryError = {...error};
        const { status } = deliveryError.response;

        if (status === 409) {
          deliveryError.type = 'cartChange'
          deliveryError.variant = 'warning'
        } else {
          deliveryError.type = 'deliveryAddress'
          if (status == 400) {
            deliveryError.response.data.title = `Delivery unavailable to ${get(deliveryAddress, 'nickname', 'unknown address')}.`;
          }
        }
        vm.dispatch('errors/HANDLE_ERROR', deliveryError)
        vm.commit('timeslots/SET_FETCHING_TIMESLOTS', false)

        return error.response
      })
  }
}

// mutations - Must be synchronous
const mutations = {
  /**
   * cache a list of addresses for this user
   * @param {array} addresses - list of addresses
   */
  SET_ADDRESSES_LIST (state, addresses) {
    // make sure addresses all have a nickname
    addresses.forEach(address => {
      if (!address.nickname) {
        address.nickname = address.address1
      }
    })

    Vue.set(state, 'list', addresses)
  },

  /**
   * cache a store
   * @param {array} addresses - list of addresses
   */
  SET_STORE (state, store) {
    Vue.set(state, 'store', store);
  },

  /**
   * set delivery address
   * @param {object} address - my reservation address
   */
  SET_NEW_ADDRESS (state, address) {
    // set address name
    if (!address.nickname) {
      address.nickname = address.name || address.address1 || ''
    }

    if (!address.name) {
      address.name = address.nickname || ''
    }

    Vue.set(state, 'new', address)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
